import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { LanguageToggle } from '../../_shared/LanguageToggle';
import { APP_VERSION } from '../../_shared/appVersion';

export function PasswordResetSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginBtnRef = useRef<HTMLButtonElement>(null);

  // Focus login button on mount for keyboard accessibility
  useEffect(() => {
    loginBtnRef.current?.focus();
  }, []);

  const handleGoToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleGoToLogin();
      }
    },
    [handleGoToLogin]
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-8)',
        position: 'relative',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          padding: 'var(--spacing-8)',
          width: '100%',
          maxWidth: '420px',
          position: 'relative',
          textAlign: 'center',
        }}
        role="main"
        aria-label={t('pwReset.success.ariaLabel')}
      >
        {/* Theme + Language toggles */}
        <div
          style={{
            position: 'absolute',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <LanguageToggle size="sm" />
          <ThemeToggle />
        </div>

        {/* Success icon */}
        <div
          aria-hidden="true"
          style={{
            width: '72px',
            height: '72px',
            borderRadius: 'var(--radius-full)',
            background:
              'color-mix(in srgb, var(--color-success) 18%, var(--color-surface-elevated))',
            border: '2px solid var(--color-success)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--spacing-4)',
          }}
        >
          <span
            style={{
              fontSize: '36px',
              color: 'var(--color-success)',
              lineHeight: 1,
            }}
          >
            ✓
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-3)',
          }}
        >
          {t('pwReset.success.title')}
        </h1>

        {/* Description */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.7,
            margin: '0 0 var(--spacing-6)',
            whiteSpace: 'pre-line' as const,
          }}
        >
          {t('pwReset.success.description')}
        </p>

        {/* Login button */}
        <button
          ref={loginBtnRef}
          type="button"
          onClick={handleGoToLogin}
          onKeyDown={handleKeyDown}
          aria-label={t('pwReset.success.button.login')}
          style={{
            display: 'block',
            width: '100%',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('pwReset.success.button.login')}
        </button>
      </div>

      {/* Footer */}
      <div
        aria-label={t('pwReset.footer.aria')}
        style={{
          marginTop: 'var(--spacing-6)',
          display: 'flex',
          gap: 'var(--spacing-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap' as const,
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <span>v{APP_VERSION}</span>
        <span>{t('pwReset.footer.copyright')}</span>
      </div>

      <style>{`
        @media (max-width: 767px) {
          button { font-size: var(--font-size-sm); }
        }
      `}</style>
    </div>
  );
}
